import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { UsersService } from 'src/app/services/users.service';
import { RequirementsService } from 'src/app/services/requirements.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-requirement-reassignment',
  templateUrl: './requirement-reassignment.component.html',
  styleUrls: ['./requirement-reassignment.component.scss'],
})
export class RequirementReassignmentComponent implements OnInit, OnDestroy {
  @Output() goBack = new EventEmitter<void>();

  // Requirement workflow properties
  selectedRequirement: any = null;

  // User list properties
  userList: any[] = [];
  filteredUserList: any[] = [];
  selectedUsers: any[] = [];
  userSearchText = '';
  selectAllUsers = false;

  // Requirements properties
  requirements: any[] = [];

  // Progress tracking
  isProcessing = false;
  progressMessage = '';
  progressPercentage = 0;
  estimatedTimeRemaining = 0;

  // Helper method for template
  Math = Math;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private usersService: UsersService,
    private requirementsService: RequirementsService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadRequirements();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onBackClick(): void {
    this.reset();
    this.goBack.emit();
  }

  // Load requirements
  loadRequirements = () => {
    const requirementsSubscription =
      this.requirementsService.requirementsData$.subscribe(
        (requirementsData: any) => {
          if (requirementsData?.rows) {
            this.requirements = requirementsData.rows;
          }
        }
      );
    this.subscriptions.push(requirementsSubscription);

    this.requirementsService.getRequirementsChildrenIncluded();
  };

  // Requirement selection methods
  onRequirementSelected = (requirement: any) => {
    this.selectedRequirement = requirement;
    this.loadAllStaff();
  };

  loadAllStaff = () => {
    this.userList = [];
    this.filteredUserList = [];

    const userSubscription = this.usersService.usersData$.subscribe(
      (userData: any) => {
        if (userData?.rows) {
          this.userList = userData.rows;
          this.filteredUserList = [...this.userList];
        }
      }
    );
    this.subscriptions.push(userSubscription);

    const params = {
      userNameFilter: '',
      activeFlag: 1 as 0 | 1,
      sortColumn: 'firstName',
      sortDirection: 'ASC' as 'ASC' | 'DESC',
      limit: 1000,
      offset: 0,
      includedAssociations:
        'PrimaryBuilding, PrimaryPosition, Roles, PrimaryApprovers, SecondaryApprovers, ManagedPrimaryUsers, ManagedSecondaryUsers',
      showAssignedToMe: 0,
      customFlag: 0,
    };

    this.usersService.getUsers(params);
  };

  // User search functionality
  onUserSearchChange = (searchText: string) => {
    this.userSearchText = searchText;

    if (!searchText || searchText.trim() === '') {
      this.filteredUserList = [...this.userList];
    } else {
      this.filteredUserList = this.userList.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
  };

  // User selection methods
  toggleUserSelection = (user: any) => {
    const index = this.selectedUsers.findIndex((u) => u.id === user.id);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user);
    }
  };

  toggleSelectAllUsers = () => {
    this.selectAllUsers = !this.selectAllUsers;
    if (this.selectAllUsers) {
      this.selectedUsers = [...this.filteredUserList];
    } else {
      this.selectedUsers = [];
    }
  };

  isUserSelected = (user: any) => {
    return this.selectedUsers.some((u) => u.id === user.id);
  };

  // Assign requirements to selected users
  assignRequirementsToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedRequirement) {
      this.toastService.setToast({
        text: 'Please select both users and a requirement to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    this.isProcessing = true;
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = Math.max(30, this.selectedUsers.length * 0.5);
    this.progressMessage = `Processing bulk assignment for ${this.selectedUsers.length} users...`;

    setTimeout(() => {
      const progressElement = document.getElementById('progress-indicator');
      if (progressElement) {
        progressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    this.toastService.setToast({
      text: `🔄 Starting bulk assignment for ${this.selectedUsers.length} users... Estimated time: ${this.estimatedTimeRemaining} seconds. Please do not navigate away.`,
      type: 'info',
      icon: true,
    });

    const progressInterval = setInterval(() => {
      if (this.isProcessing) {
        this.progressPercentage = Math.min(
          95,
          this.progressPercentage + Math.random() * 10
        );
        this.estimatedTimeRemaining = Math.max(
          0,
          this.estimatedTimeRemaining - 2
        );

        this.progressMessage = `Processing bulk assignment for ${
          this.selectedUsers.length
        } users... ${Math.round(this.progressPercentage)}% complete`;
      }
    }, 2000);

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.isProcessing) {
        event.preventDefault();
        event.returnValue =
          'Bulk assignment in progress. Are you sure you want to leave?';
        return 'Bulk assignment in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    const userIds = this.selectedUsers.map((user) => Number(user.id));
    const requirementData = {
      requirementId: Number(
        this.selectedRequirement.requirementId || this.selectedRequirement.id
      ),
      requiredValue: Number(
        this.selectedRequirement.Requirement?.trackingValue ||
          this.selectedRequirement.trackingValue
      ),
    };

    const bulkRequest = this.usersService.bulkAssignRequirements(
      userIds,
      requirementData
    );

    const timeout = setTimeout(() => {
      console.error(
        'Bulk request timed out after 10 seconds - this suggests the endpoint is not working'
      );
    }, 10000);

    bulkRequest.subscribe({
      next: (response: any) => {
        clearTimeout(timeout);
        this.waitForAssignmentsToComplete(
          userIds,
          requirementData,
          progressInterval,
          beforeUnloadHandler
        );
      },
      error: (error: any) => {
        clearTimeout(timeout);
        clearInterval(progressInterval);
        window.removeEventListener('beforeunload', beforeUnloadHandler);
        this.isProcessing = false;
        this.progressMessage = '';

        console.error('Error in bulk assignment:', error);
        this.toastService.setToast({
          text: `❌ Failed to assign requirement "${
            this.selectedRequirement.requirement ||
            this.selectedRequirement.name
          }" to users. Please try again.`,
          type: 'error',
          icon: true,
        });

        this.reset();
        this.goBack.emit();
      },
    });
  };

  waitForAssignmentsToComplete = (
    userIds: number[],
    requirementData: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) => {
    const baseWaitTime = 5000;
    const perUserWaitTime = Math.min(1000, Math.max(200, userIds.length * 50));
    const totalWaitTime = Math.min(30000, baseWaitTime + perUserWaitTime);

    this.progressMessage = `Processing ${userIds.length} user assignments... Please wait...`;

    setTimeout(() => {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);

      this.progressPercentage = 100;
      this.progressMessage = 'Bulk assignment completed successfully!';

      setTimeout(() => {
        this.isProcessing = false;
        this.progressMessage = '';
        this.progressPercentage = 0;
      }, 1000);

      this.toastService.setToast({
        text: `✅ Successfully assigned requirement "${
          this.selectedRequirement.requirement || this.selectedRequirement.name
        }" to all ${this.selectedUsers.length} users!`,
        type: 'success',
        icon: true,
      });

      this.usersService.clearSelectedUser();
      this.reset();
      this.goBack.emit();
    }, totalWaitTime);
  };

  reset = () => {
    this.selectedRequirement = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.requirements = [];
    this.isProcessing = false;
    this.progressMessage = '';
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = 0;
  };
}

